import type { CSSProperties } from 'react';
import type { DocumentCoverImage } from '../application/documentBranding';
import { COVER_IMAGE_WIDTH_PERCENT, coverImageTileCount } from '../application/coverImageLayout';
import { logoJustifyContent } from '../application/logoLayout';

interface Props {
  image: DocumentCoverImage;
}

const IMAGE_STYLE: CSSProperties = { display: 'block' };

/**
 * The cover page's own image, drawn behind the cover text (HTML preview). The react-pdf twin is
 * `CoverImageLayerPdf`; both read their layout from `coverImageLayout.ts`. The parent cover section
 * must be a stacking context (`position: relative; isolation: isolate`) so the layer sits above the
 * block background and below the text.
 */
export function CoverImageLayer({ image }: Props) {
  const widthPercent = `${COVER_IMAGE_WIDTH_PERCENT[image.size]}%`;
  const labelled = image.altText.length > 0;

  return (
    <div
      data-testid="document-render:cover-page:image"
      role={labelled ? 'img' : undefined}
      aria-label={labelled ? image.altText : undefined}
      aria-hidden={labelled ? undefined : true}
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: -1,
        overflow: 'hidden',
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: image.repeat ? 'wrap' : 'nowrap',
        alignContent: 'flex-start',
        alignItems: 'flex-start',
        justifyContent: logoJustifyContent(image.alignment)
      }}
    >
      {Array.from({ length: image.repeat ? coverImageTileCount(image.size) : 1 }, (_, index) => (
        <img key={index} src={image.url} alt="" style={{ ...IMAGE_STYLE, width: widthPercent, height: 'auto', flexShrink: 0 }} />
      ))}
    </div>
  );
}
